import { Container, Graphics, Sprite, Ticker, type Text } from 'pixi.js';
import gsap from 'gsap';
import { BOARD, COLORS, FONT, HUD, SCREEN, SHAPE, TIMING } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { BoardView } from '../render/BoardView';
import { Fx, reducedMotion } from '../render/fx';
import { background, frame } from '../assets/loader';
import { BACKGROUNDS, FRAMES, dogFrame, tileFrame } from '../assets/names';
import { after, boosterButton, button, currencyChip, iconButton, label, roundedPanel, spriteFit, textLink, toast, type BoosterButton } from '../ui/components';
import { openModal, type Modal } from '../ui/modal';
import { Game, type Step } from '../core/game';
import type { LevelDef } from '../core/level';
import type { Pos } from '../core/types';
import { sound } from '../audio/sound';
import { haptic } from '../audio/haptics';
import { settings, updateSettings } from '../settings';
import { boosterAvailable, type Booster, type WinResult } from '../meta/progress';

/** Praise for long cascades and combos, strongest wins. */
const PRAISE = ['Sweet!', 'Yummy!', 'Delicious!', 'Pawsome!'];
const MAX_FLIGHTS = 8;
/** level 1 tutorial move (mockup 23): croissant R4/C3 down to R5/C3, 0-based */
const TUTORIAL: [Pos, Pos] = [{ row: 3, col: 2 }, { row: 4, col: 2 }];
const randomSeed = () => Math.floor(Math.random() * 1e9);

/** What the gameplay screen needs from the app (save game, navigation). */
export interface GameplayHost {
  /** show the level-1 finger tutorial */
  tutorial: boolean;
  /** booster counts; null = unlimited (editor test levels, debug) */
  boosters(): Record<Booster, number> | null;
  useBooster(b: Booster): boolean;
  coins(): number;
  spendCoins(n: number): boolean;
  /** records the win; null when nothing is saved (editor test level) */
  won(stars: number): WinResult | null;
  continueAfterWin(r: WinResult | null): void;
  /** leave the level (give up / back to café) */
  quit(): void;
  canQuitToCafe: boolean;
  /** coins for +5 moves after running out, given how many continues this attempt already used */
  continueCost(usedThisAttempt: number): number;
  /** moves for watching a rewarded video after running out */
  videoMoves: number;
  /** plays a rewarded video; true only when it was watched to the end */
  watchAd(): Promise<boolean>;
  /** small coin offer when coins are not enough; true when coins were bought */
  buyCoins(parent: Container): Promise<boolean>;
}

interface GoalView {
  icon: Sprite;
  /** resting scale of the icon (bumps always return to it) */
  base: number;
  num: Text;
  shown: number;
}

/**
 * Gameplay after mockup 22, level-1 onboarding after mockup 23, win and
 * out-of-moves panels after mockups 24 and 25. The HUD shows goal counts
 * as they are *seen*: collected pieces fly to the goal panel and count down
 * on arrival; refresh() re-syncs with the real game state after every turn.
 */
export class GameplayScreen implements Screen {
  readonly view = new Container();
  private readonly bg: Sprite;
  private readonly top = new Container();
  private readonly bottom = new Container();
  private readonly fx = new Fx();
  /** above the HUD: goal flights must land on the panel */
  private readonly flightFx = new Fx();
  private readonly overlay = new Container();
  private readonly tutorialLayer = new Container();
  private game: Game;
  private readonly board: BoardView;
  private movesNum!: Text;
  private goalViews: GoalView[] = [];
  private boosterButtons = new Map<Booster, BoosterButton>();
  private dog!: Sprite;
  private dogScale = 1;
  private praiseLevel = -1;
  private l?: Layout;
  private modal?: Modal;
  private tutorialActive: boolean;
  /** "Watch video for +3" is offered once per attempt */
  private videoUsed = false;
  /** coin continues used in this attempt (the second one costs more) */
  private continues = 0;
  private winResult: WinResult | null = null;
  private handLoop?: gsap.core.Timeline;

  constructor(private readonly level: LevelDef, private readonly host: GameplayHost) {
    this.bg = new Sprite(background(level.location === 'paris' ? BACKGROUNDS.gameplayParis : BACKGROUNDS.gameplayCafe));
    this.game = new Game(level, randomSeed());
    this.board = new BoardView(this.game, this.fx);
    this.tutorialActive = host.tutorial;
    this.buildHud();
    this.buildBottom();
    this.board.onChange = () => this.refresh();
    this.board.onStep = (s) => this.onStep(s);
    this.view.addChild(this.bg, this.board.view, this.fx.view, this.bottom, this.top, this.tutorialLayer, this.flightFx.view, this.overlay);
    if (this.tutorialActive) this.startTutorial();
    this.refresh();
    this.debugHook();
  }

  destroy(): void {
    this.handLoop?.kill();
    this.board.destroy();
    this.fx.destroy();
    this.flightFx.destroy();
  }

  /** window.cafePaws – used by the automated browser tests */
  private debugHook(): void {
    (window as unknown as { cafePaws: unknown }).cafePaws = {
      game: () => this.game,
      mismatches: () => this.board.mismatches(),
      setup: (board: string[]) => {
        this.game = new Game({ ...this.level, board }, 1);
        this.board.setGame(this.game);
        this.refresh();
      },
      slowmo: (f: number) => {
        gsap.globalTimeline.timeScale(f);
        Ticker.shared.speed = f;
      },
      /** test helpers: end the level right away */
      forceWin: () => {
        this.game.goals.forEach((g) => (g.remaining = 0));
        this.game.status = 'won';
        this.refresh();
      },
      forceLose: () => {
        this.game.movesLeft = 0;
        this.game.status = 'lost';
        this.refresh();
      },
      mismatchDetails: () => {
        const d: string[] = [];
        this.board.mismatches(d);
        return d;
      },
      cellToScreen: (row: number, col: number) =>
        this.board.view.toGlobal({
          x: BOARD.inner.x - BOARD.frame.x + (col + 0.5) * BOARD.cell,
          y: BOARD.inner.y - BOARD.frame.y + (row + 0.5) * BOARD.cell,
        }),
    };
  }

  // ------------------------------------------------------------ HUD

  private buildHud(): void {
    const title = label(`Level ${this.level.id}`, FONT.size.screenTitle, { weight: FONT.weight.heading });
    title.position.set(SCREEN.width / 2, HUD.titleY);
    const pause = iconButton('pause', () => this.showPause(), undefined, 72);
    pause.position.set(HUD.goals.x + HUD.goals.w - 36, HUD.titleY);

    const m = HUD.moves;
    const moves = new Container();
    moves.position.set(m.x, m.y);
    const movesLabel = label('Moves', FONT.size.secondary);
    movesLabel.position.set(m.w / 2, 38);
    this.movesNum = label('', FONT.size.movesCounter, { weight: FONT.weight.heading });
    this.movesNum.position.set(m.w / 2, 106);
    moves.addChild(roundedPanel(m.w, m.h), movesLabel, this.movesNum);

    const g = HUD.goals;
    const goals = new Container();
    goals.position.set(g.x, g.y);
    const goalsLabel = label(this.game.goals.length > 1 ? 'Goals' : 'Goal', FONT.size.secondary);
    goalsLabel.position.set(g.w / 2, 38);
    goals.addChild(roundedPanel(g.w, g.h), goalsLabel);
    // one chip per goal, spread over the panel
    const n = this.game.goals.length;
    this.goalViews = this.game.goals.map((goal, i) => {
      const cx = (g.w / n) * (i + 0.5) - 40;
      const iconName = goal.def.type === 'collect' ? tileFrame(goal.def.kind) : goal.def.type === 'mud' ? FRAMES.mud2 : FRAMES.pawStain;
      const icon = spriteFit(iconName, 80);
      icon.anchor.set(0.5);
      icon.position.set(cx, 108);
      const num = label('', 56, { weight: FONT.weight.heading, anchor: [0, 0.5] });
      num.position.set(cx + 50, 110);
      goals.addChild(icon, num);
      return { icon, base: icon.scale.x, num, shown: goal.remaining };
    });

    this.top.addChild(title, pause, moves, goals);
  }

  private buildBottom(): void {
    const b = HUD.boosters;
    const bar = new Container();
    bar.position.set(b.x, b.y);
    const counts = this.host.boosters();
    const available = (['hammer', 'spoon', 'moves5'] as Booster[]).filter((k) => counts === null || boosterAvailable(this.level.id, k));
    if (available.length) {
      const w = 90 + available.length * 130;
      bar.addChild(roundedPanel(w, b.h, { radius: 40 }));
      const icons: Record<Booster, string> = { hammer: FRAMES.hammer, spoon: FRAMES.spoon, moves5: FRAMES.moves5 };
      available.forEach((k, i) => {
        const btn = boosterButton(icons[k], counts ? counts[k] : null, () => this.useBooster(k));
        btn.view.position.set(90 + i * 130, b.h / 2);
        bar.addChild(btn.view);
        this.boosterButtons.set(k, btn);
      });
    }

    // Honey cheers next to the bar and reacts to good moves
    const dog = new Sprite(frame(dogFrame('honey', 'happy')));
    dog.anchor.set(0.5, 1);
    this.dogScale = HUD.dog.height / dog.texture.height;
    dog.scale.set(this.dogScale);
    dog.position.set(HUD.dog.x - b.x, HUD.dog.bottom - b.y);
    this.breathe(dog);
    dog.eventMode = 'static';
    dog.on('pointertap', () => this.dogJump(1));
    this.dog = dog;
    bar.addChild(dog);
    this.bottom.addChild(bar);
  }

  private refreshBoosters(): void {
    const counts = this.host.boosters();
    for (const [k, btn] of this.boosterButtons) btn.setCount(counts ? counts[k] : null);
  }

  private useBooster(k: Booster): void {
    if (this.board.isBusy || this.game.status !== 'playing' || this.tutorialActive) return;
    const counts = this.host.boosters();
    if (counts && counts[k] <= 0) {
      toast(this.overlay, 'More boosters in the shop soon!', (this.l?.safeTop ?? 92) + 380, `booster_${k === 'moves5' ? 'moves5' : k}`);
      return;
    }
    if (k === 'hammer') return this.armHammer();
    if (!this.host.useBooster(k)) return;
    this.refreshBoosters();
    if (k === 'spoon') void this.board.run(() => this.game.useShuffle().steps);
    else {
      this.game.addMoves(5);
      sound.play('create');
      haptic('medium');
      this.setMoves(this.game.movesLeft, true);
    }
  }

  private armHammer(): void {
    const btn = this.boosterButtons.get('hammer')!.view;
    if (this.board.onCellTap) return this.disarmHammer();
    gsap.to(btn.scale, { x: 1.15, y: 1.15, duration: 0.15 });
    this.board.onCellTap = (p) => {
      this.disarmHammer();
      if (!this.host.useBooster('hammer')) return;
      this.refreshBoosters();
      const at = this.board.cellPoint(p);
      this.fx.shockwave(at.x, at.y, 90, COLORS.wood);
      haptic('heavy');
      void this.board.run(() => this.game.useHammer(p).steps);
    };
  }

  private disarmHammer(): void {
    this.board.onCellTap = undefined;
    const btn = this.boosterButtons.get('hammer');
    if (btn) gsap.to(btn.view.scale, { x: 1, y: 1, duration: 0.15 });
  }

  /** §13 idle breathing: 2.5–3.5 s, very small scale change */
  private breathe(dog: Sprite): void {
    gsap.to(dog.scale, { y: this.dogScale * 1.025, x: this.dogScale * 0.99, duration: TIMING.breathe / 2000, yoyo: true, repeat: -1, ease: 'sine.inOut' });
  }

  private dogJump(times: number, hearts = true): void {
    const d = this.dog;
    const s = this.dogScale;
    const baseY = HUD.dog.bottom - HUD.boosters.y;
    gsap.killTweensOf(d);
    gsap.killTweensOf(d.scale);
    const tl = gsap.timeline({ onComplete: () => this.breathe(d) });
    for (let i = 0; i < times; i++) {
      tl.to(d.scale, { x: s * 1.1, y: s * 0.88, duration: 0.08 })
        .to(d.scale, { x: s * 0.94, y: s * 1.08, duration: 0.12 })
        .to(d, { y: baseY - (reducedMotion ? 10 : 55), duration: 0.2, ease: 'power2.out' }, '<')
        .to(d, { y: baseY, duration: 0.2, ease: 'power2.in' })
        .to(d.scale, { x: s * 1.1, y: s * 0.9, duration: 0.06 })
        .to(d.scale, { x: s, y: s, duration: 0.25, ease: 'elastic.out(1, 0.5)' });
    }
    if (hearts) {
      const p = this.view.toLocal(d.getGlobalPosition());
      this.flightFx.burst(p.x, p.y - HUD.dog.height * 0.9, { frame: 'vfx_heart', count: 3, speed: 120, gravity: -140, size: 34, life: 0.7, angle: -Math.PI / 2, spread: 1.4 });
    }
  }

  // ------------------------------------------------------------ onboarding (mockup 23)

  private startTutorial(): void {
    const [a, b] = TUTORIAL;
    const same = (p: Pos, q: Pos) => p.row === q.row && p.col === q.col;
    this.board.allowSwap = (x, y) => (same(x, a) && same(y, b)) || (same(x, b) && same(y, a));
    this.board.hints = false;
    const banner = new Container();
    const t = label('Swipe to match 3', FONT.size.body + 4, { weight: FONT.weight.heading });
    const w = t.width + 80;
    banner.addChild(new Graphics().roundRect(-w / 2, -34, w, 68, 34).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa }), t);
    banner.label = 'banner';
    this.tutorialLayer.addChild(banner);
    this.tutorialLayer.addChild(this.handPointer());
  }

  /** Pointing finger drawn in code (no hand asset): cream glove with cocoa outline. */
  private handPointer(): Container {
    const c = new Container();
    const g = new Graphics();
    const ink = { width: 5, color: COLORS.cocoa };
    g.roundRect(-14, -8, 28, 70, 14).fill(COLORS.cream).stroke(ink); // index finger
    g.roundRect(-30, 34, 70, 64, 26).fill(COLORS.cream).stroke(ink); // palm
    g.roundRect(-44, 44, 26, 40, 13).fill(COLORS.cream).stroke(ink); // thumb
    g.roundRect(-14, -8, 28, 30, 14).fill(COLORS.cream);
    g.moveTo(-14, 10).lineTo(-14, 40).moveTo(14, 10).lineTo(14, 38).stroke(ink);
    c.addChild(g);
    c.label = 'hand';
    // highlight frames for the two cells
    const hl = new Graphics();
    for (const p of TUTORIAL) hl.roundRect(p.col * BOARD.cell + 4, p.row * BOARD.cell + 4, BOARD.cell - 8, BOARD.cell - 8, 16).stroke({ width: 6, color: COLORS.raspberry });
    hl.label = 'highlight';
    const wrap = new Container();
    wrap.addChild(hl, c);
    wrap.label = 'pointer';
    return wrap;
  }

  private positionTutorial(): void {
    const banner = this.tutorialLayer.getChildByLabel('banner');
    const pointer = this.tutorialLayer.getChildByLabel('pointer') as Container | null;
    if (!banner || !pointer) return;
    const origin = this.board.cellPoint({ row: -0.5, col: -0.5 });
    banner.position.set(SCREEN.width / 2, origin.y - 60);
    pointer.position.set(origin.x, origin.y);
    const hand = pointer.getChildByLabel('hand')!;
    const [a, b] = TUTORIAL;
    const pa = { x: (a.col + 0.5) * BOARD.cell + 10, y: (a.row + 0.5) * BOARD.cell };
    const pb = { x: (b.col + 0.5) * BOARD.cell + 10, y: (b.row + 0.5) * BOARD.cell };
    this.handLoop?.kill();
    hand.position.set(pa.x, pa.y);
    hand.alpha = 1;
    this.handLoop = gsap.timeline({ repeat: -1, repeatDelay: 0.5 })
      .fromTo(hand, { alpha: 0 }, { alpha: 1, duration: 0.2 })
      .to(hand, { x: pb.x, y: pb.y, duration: 0.7, ease: 'power1.inOut' }, '+=0.25')
      .to(hand, { alpha: 0, duration: 0.25 }, '+=0.2')
      .set(hand, { x: pa.x, y: pa.y });
  }

  private endTutorial(): void {
    this.tutorialActive = false;
    this.board.allowSwap = undefined;
    this.board.hints = true;
    this.handLoop?.kill();
    this.handLoop = undefined;
    for (const c of [...this.tutorialLayer.children]) {
      gsap.killTweensOf(c);
      gsap.to(c, { alpha: 0, duration: 0.25, onComplete: () => c.destroy({ children: true }) });
    }
    const c = this.board.cellPoint({ row: 8, col: 3.5 });
    const t = label('Your first match!', FONT.size.body + 4, { weight: FONT.weight.heading });
    t.position.set(c.x, c.y + 40);
    this.tutorialLayer.addChild(t);
    gsap.fromTo(t.scale, { x: 0.5, y: 0.5 }, { x: 1, y: 1, duration: 0.4, ease: 'back.out(2.5)' });
    gsap.to(t, { alpha: 0, duration: 0.4, delay: 2.2, onComplete: () => t.destroy() });
  }

  // ------------------------------------------------------------ turn feedback

  private onStep(step: Step): void {
    switch (step.type) {
      case 'swap':
        this.praiseLevel = -1;
        this.setMoves(this.game.movesLeft, true);
        break;
      case 'clear':
        if (this.tutorialActive && step.cascade === 1) this.endTutorial();
        this.flyToGoals(step);
        this.praiseFor(step);
        break;
      case 'end':
        if (step.status === 'won') {
          const l = this.l;
          this.flightFx.confetti(l?.left ?? 0, (l?.right ?? SCREEN.width) - (l?.left ?? 0), -20, 90);
          sound.play('win');
          haptic('success');
          this.dogJump(3);
        } else {
          sound.play('lose');
          haptic('warning');
        }
        break;
    }
  }

  private setMoves(n: number, bump: boolean): void {
    const changed = this.movesNum.text !== String(n);
    this.movesNum.text = String(n);
    // few moves left: raspberry number (no pulsing, §5)
    this.movesNum.style.fill = n <= 5 ? COLORS.raspberry : COLORS.cocoa;
    if (bump && changed) {
      gsap.fromTo(this.movesNum.scale, { x: 1.25, y: 0.8 }, { x: 1, y: 1, duration: 0.4, ease: 'elastic.out(1, 0.45)' });
    }
  }

  /** Collected goal pieces fly to their chip; the counter ticks down on arrival. */
  private flyToGoals(step: Step & { type: 'clear' }): void {
    this.game.goals.forEach((goal, i) => {
      const gv = this.goalViews[i];
      const target = this.view.toLocal(gv.icon.getGlobalPosition());
      let sources: { x: number; y: number }[] = [];
      if (goal.def.type === 'collect') {
        const kind = goal.def.kind;
        sources = step.cleared.filter((c) => c.piece.kind === kind).map((c) => this.board.cellPoint(c.pos));
      } else if (goal.def.type === 'mud') {
        sources = step.mudHits.filter((h) => h.hpLeft <= 0).map((h) => this.board.cellPoint(h.pos));
      } else {
        sources = step.stainsCleared.map((p) => this.board.cellPoint(p));
      }
      const needed = Math.min(sources.length, gv.shown);
      if (needed <= 0) return;
      const flights = Math.min(needed, MAX_FLIGHTS);
      const tex = gv.icon.texture;
      for (let k = 0; k < flights; k++) {
        // split the count over the flights: 11 croissants in 8 flights = 2,2,2,1,1,1,1,1
        const amount = Math.floor(needed / flights) + (k < needed % flights ? 1 : 0);
        this.flightFx.fly(tex, sources[k], target, 70, 0.12 + k * 0.05, () => {
          // never show less than the real remaining count
          gv.shown = Math.max(goal.remaining, gv.shown - amount);
          this.showGoal(gv, true);
        });
      }
    });
  }

  private showGoal(gv: GoalView, bump: boolean): void {
    const done = gv.shown <= 0;
    const text = done ? '✓' : String(gv.shown);
    if (gv.num.text === text) return;
    gv.num.text = text;
    gv.num.style.fill = done ? COLORS.raspberry : COLORS.cocoa;
    if (!bump) return;
    gsap.fromTo(gv.icon.scale, { x: gv.base * 1.3, y: gv.base * 1.3 }, { x: gv.base, y: gv.base, duration: 0.3, ease: 'back.out(3)', overwrite: true });
    if (done) {
      sound.play('goal');
      haptic('success');
      const p = this.view.toLocal(gv.icon.getGlobalPosition());
      this.flightFx.burst(p.x, p.y, { frame: 'vfx_star', count: 8, speed: 300, gravity: 300, size: 28 });
    } else sound.play('tap', { pitch: 1.5, volume: 0.5 });
  }

  private praiseFor(step: Step & { type: 'clear' }): void {
    let level = -1;
    if (step.combo) level = 3;
    else if (step.cascade >= 5) level = 2;
    else if (step.cascade >= 4 || step.activated.some((a) => a.piece.special === 'colorstar')) level = 1;
    else if (step.cascade >= 3) level = 0;
    if (level <= this.praiseLevel) return;
    this.praiseLevel = level;
    const c = this.board.cellPoint({ row: 3.5, col: 3.5 });
    this.fx.praise(PRAISE[level], c.x, c.y, 76 + level * 6);
    sound.play('praise');
    this.dogJump(1);
  }

  // ------------------------------------------------------------ state

  private refresh(): void {
    this.setMoves(this.game.movesLeft, false);
    this.game.goals.forEach((g, i) => {
      this.goalViews[i].shown = g.remaining;
      this.showGoal(this.goalViews[i], false);
    });
    this.refreshBoosters();
    if (this.game.status === 'won' && !this.winResult) {
      // record right away, so closing the app on the win panel keeps the progress
      this.winResult = this.host.won(this.game.stars()) ?? null;
    }
    if (this.game.status !== 'playing') {
      after(this.view, this.game.status === 'won' ? 1000 : 600, () => this.game.status !== 'playing' && this.showEnd());
    }
  }

  private openPanel(w: number, h: number): Container {
    this.modal?.close();
    this.modal = openModal(this.overlay, this.l, w, h);
    return this.modal.panel;
  }

  private closePanel(): void {
    this.modal?.close();
    this.modal = undefined;
  }

  private showEnd(): void {
    if (this.game.status === 'won') this.showWin();
    else this.showOutOfMoves();
  }

  /** Mockup 24: "Lovely work!", stars, Honey jumping for joy, coins, Continue. */
  private showWin(): void {
    const w = 760;
    const h = 1040;
    const panel = this.openPanel(w, h);
    const title = label('Lovely work!', FONT.size.celebration + 4, { weight: FONT.weight.heading });
    title.position.set(w / 2, 100);
    panel.addChild(title);
    const stars = this.game.stars();
    for (let i = 0; i < 3; i++) {
      const s = spriteFit('vfx_star', i === 1 ? 150 : 124);
      s.anchor.set(0.5);
      s.position.set(w / 2 + (i - 1) * 165, i === 1 ? 230 : 250);
      const sc = s.scale.x;
      if (i < stars) {
        s.scale.set(0);
        gsap.to(s.scale, { x: sc, y: sc, duration: 0.45, delay: 0.35 + (i * TIMING.starGap) / 1000, ease: 'back.out(3)', onStart: () => sound.play('create', { pitch: 1 + i * 0.12 }) });
      } else {
        s.tint = COLORS.cocoa;
        s.alpha = 0.18;
      }
      panel.addChild(s);
    }
    // Honey jumping for joy
    const honey = new Sprite(frame(dogFrame('honey', 'happy')));
    honey.anchor.set(0.5, 1);
    const hs = 360 / honey.texture.height;
    honey.scale.set(hs);
    honey.position.set(w / 2, 700);
    panel.addChild(honey);
    if (!reducedMotion) {
      gsap.timeline({ repeat: -1, repeatDelay: 0.5 })
        .to(honey.scale, { x: hs * 1.08, y: hs * 0.9, duration: 0.1 })
        .to(honey, { y: 640, rotation: -0.06, duration: 0.25, ease: 'power2.out' })
        .to(honey.scale, { x: hs * 0.96, y: hs * 1.06, duration: 0.25 }, '<')
        .to(honey, { y: 700, rotation: 0, duration: 0.22, ease: 'power2.in' })
        .to(honey.scale, { x: hs, y: hs, duration: 0.3, ease: 'elastic.out(1, 0.5)' });
    }
    const r = this.winResult;
    const coins = r?.coinsEarned ?? 0;
    const row = new Container();
    const chip = currencyChip('ui_coin', 0);
    chip.view.scale.set(1.35);
    row.addChild(chip.view);
    chip.set(coins);
    if (r && r.starsEarned > 0) {
      const st = currencyChip('vfx_star', 0);
      st.view.scale.set(1.35);
      chip.view.x = -150;
      st.view.x = 150;
      row.addChild(st.view);
      after(this.view, 400, () => st.set(r.starsEarned));
    }
    row.position.set(w / 2, 790);
    panel.addChild(row);
    const hint = label(r && r.starsEarned > 0 ? 'Spend stars to fix up your café!' : ' ', FONT.size.secondary, { weight: FONT.weight.body });
    hint.position.set(w / 2, 858);
    panel.addChild(hint);
    const cont = button('Continue', 560, 'primary', () => this.host.continueAfterWin(this.winResult), 104);
    cont.position.set(w / 2, 950);
    panel.addChild(cont);
  }

  /** Mockup 25: hopeful, no countdown, no guilt; clearly visible "Give up". */
  private showOutOfMoves(): void {
    const w = 760;
    const h = 1060;
    const panel = this.openPanel(w, h);
    const coins = this.host.coins();
    const balance = currencyChip('ui_coin', coins);
    balance.view.scale.set(0.8);
    balance.view.position.set(w - 110, 60);
    panel.addChild(balance.view);
    const title = label('So close!', FONT.size.celebration + 8, { weight: FONT.weight.heading });
    title.position.set(w / 2, 150);
    const sub = label(this.remainingText(), FONT.size.body + 2, { weight: FONT.weight.heading });
    sub.position.set(w / 2, 230);
    const honey = new Sprite(frame(dogFrame('honey', 'happy')));
    honey.anchor.set(0.5, 1);
    const hs = 300 / honey.texture.height;
    honey.scale.set(hs);
    honey.position.set(w / 2, 590);
    gsap.to(honey.scale, { y: hs * 1.03, duration: 1.4, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    panel.addChild(title, sub, honey);

    const free = this.host.boosters() === null; // editor / debug
    const cost = this.host.continueCost(this.continues);
    const plus = new Container();
    const btn = button('', 600, 'primary', () => void this.continueWithCoins(free, cost), 110);
    const t = label('+5 Moves', FONT.size.primaryButton, { weight: FONT.weight.heading, color: 'cream', anchor: [0, 0.5] });
    const coin = spriteFit('ui_coin', 56);
    coin.anchor.set(0.5);
    const price = label(free ? 'free' : String(cost), FONT.size.primaryButton, { weight: FONT.weight.heading, color: 'cream', anchor: [0, 0.5] });
    const total = t.width + 40 + 56 + 10 + price.width;
    t.x = -total / 2;
    coin.x = -total / 2 + t.width + 40 + 28;
    price.x = coin.x + 38;
    plus.addChild(btn, t, coin, price);
    plus.position.set(w / 2, 680);
    panel.addChild(plus);

    if (!this.videoUsed) {
      const video = button(`▶  Watch video for +${this.host.videoMoves}`, 600, 'secondary', () => void this.continueWithVideo(), 96);
      video.position.set(w / 2, 810);
      panel.addChild(video);
    }
    const giveUp = textLink('Give up', () => this.host.quit());
    giveUp.position.set(w / 2, this.videoUsed ? 830 : 940);
    panel.addChild(giveUp);
  }

  private continueWith(moves: number): void {
    this.closePanel();
    this.game.addMoves(moves);
    sound.play('create');
    haptic('success');
    this.setMoves(this.game.movesLeft, true);
  }

  private async continueWithCoins(free: boolean, cost: number): Promise<void> {
    if (free) return this.continueWith(5);
    if (!this.host.spendCoins(cost)) {
      // not enough coins: offer a small coin pack right here, then continue
      if (!((await this.host.buyCoins(this.overlay)) && this.host.spendCoins(cost))) return this.showOutOfMoves();
    }
    this.continues++;
    this.continueWith(5);
  }

  private async continueWithVideo(): Promise<void> {
    this.videoUsed = true;
    // the reward is only given for a video watched to the end (§10)
    if (await this.host.watchAd()) this.continueWith(this.host.videoMoves);
    else this.showOutOfMoves();
  }

  private showPause(): void {
    const rows: [string, () => void, 'primary' | 'secondary'][] = [
      ['Resume', () => this.closePanel(), 'primary'],
      [`Sound: ${settings().sound ? 'On' : 'Off'}`, () => (updateSettings({ sound: !settings().sound }), this.showPause()), 'secondary'],
      [`Vibration: ${settings().haptics ? 'On' : 'Off'}`, () => (updateSettings({ haptics: !settings().haptics }), haptic('medium'), this.showPause()), 'secondary'],
      ['Restart level', () => this.restart(), 'secondary'],
    ];
    if (this.host.canQuitToCafe) rows.push(['Back to café', () => this.host.quit(), 'secondary']);
    const w = 640;
    const h = 180 + rows.length * 110;
    const panel = this.openPanel(w, h);
    const title = label('Paused', FONT.size.screenTitle, { weight: FONT.weight.heading });
    title.position.set(w / 2, 80);
    panel.addChild(title);
    rows.forEach(([text, onTap, variant], i) => {
      const b = button(text, 480, variant, onTap, 88);
      b.position.set(w / 2, 180 + i * 110);
      panel.addChild(b);
    });
  }

  /** Remaining goal derived from the real game state (spec §9, screen 25). */
  private remainingText(): string {
    const open = this.game.goals.filter((g) => g.remaining > 0);
    if (open.length !== 1) return 'Just a little more!';
    const g = open[0];
    if (g.def.type === 'collect') return `Only ${g.remaining} ${g.def.kind}${g.remaining === 1 ? '' : 's'} left!`;
    return `Only ${g.remaining} ${g.def.type === 'mud' ? 'mud' : 'paw'} spot${g.remaining === 1 ? '' : 's'} left!`;
  }

  private restart(): void {
    this.closePanel();
    this.winResult = null;
    this.videoUsed = false;
    this.continues = 0;
    this.game = new Game(this.level, randomSeed());
    this.board.setGame(this.game);
    this.refresh();
  }

  layout(l: Layout): void {
    this.l = l;
    // background covers the whole view, keeps its aspect ratio (§4, §12)
    const viewW = l.right - l.left;
    const s = Math.max(viewW / this.bg.texture.width, l.bottom / this.bg.texture.height);
    this.bg.scale.set(s);
    this.bg.position.set(l.left + (viewW - this.bg.texture.width * s) / 2, l.bottom - this.bg.texture.height * s);

    // HUD sticks to the top safe area, booster bar to the bottom one,
    // the board is centred in the space between (§4 "longer displays");
    // the tutorial needs a line for its hint above the board (mockup 23)
    this.top.y = l.safeTop - SCREEN.safeTop;
    this.bottom.y = l.safeBottom - (SCREEN.height - SCREEN.safeBottom);
    const hudBottom = HUD.moves.y + HUD.moves.h + this.top.y + (this.tutorialActive ? 90 : 0);
    const barTop = HUD.boosters.y + this.bottom.y;
    this.board.view.position.set(BOARD.frame.x, hudBottom + (barTop - hudBottom - BOARD.frame.h) / 2);
    this.positionTutorial();
  }
}
